// KioskPhotosCard — the building's punch-kiosk photo switch (item "Punch kiosk photos: notice,
// the worker's no-photo choice, 90-day deletion, who can open them"). Ben, 1 Nov 2026:
// "anyone in the company need[s] to be able to turn on/off the feature ... MUST BE able to be
// done by any full employee ... if a facility asks to turn it off, we can (and just have pin)."
//
// Shown on a building that has a kiosk iPad, or whose photos are off. The database decides who
// may switch it (rpc_time_kiosk_photos: any active TCS employee with an app login) and keeps the
// change in the facilities audit trail. A load error is shown, never swallowed.
import { useCallback, useEffect, useState } from 'react';
import { Camera, CameraOff } from 'lucide-react';
import { Button } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { kioskPhotosSetting, type KioskPhotosSetting } from '../../time-labor/hooks/use-time-data';

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function KioskPhotosCard({ facilityId }: { facilityId: string }) {
  const [s, setS] = useState<KioskPhotosSetting | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    kioskPhotosSetting(facilityId)
      .then((d) => { setS(d); setLoadError(null); })
      .catch((e) => setLoadError(errorMessage(e, 'The kiosk photo setting could not be read.')));
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4 text-sm text-tcs-text-muted" data-testid="kiosk-photos-card-error">
        Punch kiosk photos: {loadError}
      </div>
    );
  }
  if (!s || (s.kiosks === 0 && s.photos_on)) return null;

  async function flip(on: boolean) {
    setSaving(true); setSaveError(null);
    try {
      setS(await kioskPhotosSetting(facilityId, on));
    } catch (e) {
      setSaveError(errorMessage(e, 'It could not be switched — try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4" data-testid="kiosk-photos-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            {s.photos_on ? <Camera size={16} /> : <CameraOff size={16} />}
            Punch kiosk photos: {s.photos_on ? 'On' : 'Off'}
          </div>
          <p className="mt-1 text-sm text-tcs-text-secondary">
            {s.photos_on
              ? 'The kiosk iPad here takes one photo at each punch, unless the worker chose no photo. Photos are deleted after 90 days.'
              : `Off since ${s.off_since ? fmtDay(s.off_since) : '—'}. Workers here punch with their phone number and PIN only; no photo is taken or kept.`}
          </p>
          <p className="mt-1 text-xs text-tcs-text-muted">
            Any TCS employee can switch this — for example when the facility asks for PIN only.
          </p>
        </div>
        <Button size="sm" variant="secondary" loading={saving} onClick={() => void flip(!s.photos_on)}>
          {s.photos_on ? 'Turn photos off' : 'Turn photos on'}
        </Button>
      </div>
      {saveError && <div className="mt-2 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{saveError}</div>}
    </div>
  );
}
